import {
  Checkbox,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { useSelector } from "react-redux";
import { selectCurrentCaptain, selectCurrentTrainingAssistant, selectCurrentUser } from "../../redux/authentication/authSlice";
import { UserGroup } from "../../common/User";
import { useGetVesselQuery } from "../../redux/api/vessel";
import { FormEvent, useCallback, useEffect, useState } from "react";
import {
  useGetTrainingLocationsQuery,
  useGetTrainingsQuery,
} from "../../redux/api/training";
import {
  useGetTrainingOrdersByVesselQuery,
  useSaveTrainingOrdersMutation,
} from "../../redux/api/trainingOrder";
import useDocumentTitle from "../../util/useDocumentTitle";
import { Link, NavLink, useParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { JobRating, JobRatingLabel } from "../../common/JobOrder";
import {
  TrainingOrder,
  TrainingOrderStatus,
  TrainingOrderStatusLabel,
} from "../../common/Training";
import { formatDateString } from "../../common/FormatDateString";
import {
  Dispatch,
  DispatchSearchParams,
  TrainingRosterMember,
  TrainingRosterRow,
} from "../../common/Dispatch";
import { useGetDispatchesQuery } from "../../redux/api/dispatch";
import convertDateToString from "../../util/ConvertDateToString";
import { MarinerProfile } from "../../common/MarinerProfile";
import { usePrompt } from "../../util/usePrompt";
import { useGetPermanentOfficersQuery } from "../../redux/api/mariner";
import { Add } from "@mui/icons-material";
import { Button } from "@mui/material";

const TrainingRosterChangeForm = (props: {
  parentCallback: Function;
  changedRowsWithError: number[];
  formSubmitted: boolean;
  setFormSubmitted: (value: boolean) => void;
}) => {
  const user = useSelector(selectCurrentUser);
  const isCaptain = useSelector(selectCurrentCaptain);
  const isTrainingAssistant = useSelector(selectCurrentTrainingAssistant);
  const isViewOnly =
    (user?.userGroup as UserGroup) === UserGroup.VESSEL_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.FLEET_EXECS;
  const defaultRoster: TrainingRosterRow[] = [];
  const [roster, setRoster] = useState<TrainingRosterRow[]>(defaultRoster);
  const [changedRows, setChangedRows] = useState<number[]>([]);

  useDocumentTitle("Training Roster");
  const { id } = useParams();

  const { data: locations } = useGetTrainingLocationsQuery();
  const { data: names } = useGetTrainingsQuery();
  const [saveTrainingOrders] = useSaveTrainingOrdersMutation();

  const {
    data: vessel,
    isLoading: isVesselLoading,
    isFetching: isVesselFetching,
  } = useGetVesselQuery(id !== undefined && id !== "" ? id : skipToken, {
    refetchOnMountOrArgChange: true,
  });

  const {
    data: trainingOrders,
    isLoading: isOrdersLoading,
    isFetching: isOrdersFetching,
    refetch: refetchOrders,
  } = useGetTrainingOrdersByVesselQuery(
    id !== undefined && id !== "" ? id : skipToken,
    { refetchOnMountOrArgChange: true }
  );

  const dispatchSearchParams: DispatchSearchParams = {
    vesselId: !!id ? id : "",
    fleetId: undefined,
    marinerIds: [],
    ratings: undefined,
    current: true,
  };
  const {
    data: dispatches,
    isLoading: isDispatchesLoading,
    isFetching: isDispatchesFetching,
  } = useGetDispatchesQuery(
    id !== undefined && id !== "" ? dispatchSearchParams : skipToken
  );

  const {
    data: permanentOfficers,
    isLoading: isPermanentOfficersLoading,
    isFetching: isPermanentOfficersFetching,
  } = useGetPermanentOfficersQuery(
    id !== undefined && id !== "" ? id : skipToken,
    { refetchOnMountOrArgChange: true }
  );

  const removeChangedRows = (id: number) => {
    if (changedRows.includes(id)) {
      setChangedRows(changedRows.filter((row) => row !== id));
    }
  };

  const addChangedRows = (id: number) => {
    if (!changedRows.includes(id)) {
      let updatedRows = changedRows;
      updatedRows.push(id);
      setChangedRows(updatedRows);
    }
  };

  const toggleRowEdit = (row: TrainingRosterRow, editable: boolean) => {
    const { id } = row;
    const tempRoster = [...roster];
    let idx = tempRoster.findIndex((row) => row.id === id);
    if (idx !== -1) {
      tempRoster[idx].editing = editable;
      if (editable === false) {
        tempRoster[idx].trainingOrder = defaultTrainingOrder(
          row.trainingOrder.mariner!
        );
        removeChangedRows(id as number);
      } else {
        tempRoster[idx].trainingOrder = defaultTrainingOrder(
          row.trainingOrder.mariner!
        );
        addChangedRows(id as number);
      }
    }
    setRoster(tempRoster);
  };

  const defaultTrainingOrder = useCallback(
    (mariner: MarinerProfile) => {
      return {
        id: undefined,
        createdDate: "",
        dueDate: "",
        startDate: "",
        endDate: "",
        daysToBePaid: undefined,
        vessel:
          id !== undefined
            ? {
                id: parseInt(id),
                name: undefined,
                fleet: undefined,
                crewRequirements: undefined,
                rosRequirements: undefined,
                ros: undefined,
                ports: undefined,
              }
            : undefined,
        assignee: undefined,
        assigneeId: undefined,
        expirationDate: "",
        mariner: mariner,
        marinerLeavingFrom: "",
        marinerDestination: "",
        marinerTravelTimestamp: "",
        marinerAccomodation: "",
        marinerPickupDate: "",
        marinerDropoffDate: "",
        marinerNotes: "",
        training: undefined,
        trainingId: undefined,
        checklistRoot: undefined,
        travelDocuments: undefined,
        rating: mariner.rating!,
        flightNotice: undefined,
        trainingLocation: undefined,
        locationId: undefined,
        comments: "",
        status: undefined,
        note: "",
      };
    },
    [id]
  );

  const convertTrainingOrdersAndDispatchesToRoster = useCallback(
    (trainingOrdersFromApi: TrainingOrder[], dispatchesFromApi: Dispatch[], permanentOfficersFromApi: MarinerProfile[]) => {
      let trainingOrders: any[] = JSON.parse(
        JSON.stringify(trainingOrdersFromApi)
      );
      let dispatches: any[] = JSON.parse(JSON.stringify(dispatchesFromApi));
      let permanentOfficers: any[] = JSON.parse(JSON.stringify(permanentOfficersFromApi));
      let trainingOrdersMap: Map<number, TrainingOrder[]> = new Map<
        number,
        TrainingOrder[]
      >();

      trainingOrders.forEach((order: TrainingOrder) => {
        if (order.dueDate != null) {
          order.dueDate = convertDateToString(new Date(order.dueDate));
        }
        if (!!order.mariner && !!order.mariner.id) {
          // currently overwrites the map for all 2+ training orders
          // if not in map, create list from order
          if (!trainingOrdersMap.has(order.mariner.id)) {
            trainingOrdersMap.set(order.mariner.id, [order]);
          }
          // else, get list and add
          else {
            trainingOrdersMap.get(order.mariner.id)?.push(order);
          }
        }
      });
      const trainingRoster: TrainingRosterRow[] = [];
      dispatches.forEach((dispatch) => {
        if (!!dispatch.profileId && trainingOrdersMap.has(dispatch.profileId)) {
          const rosterMember: TrainingRosterMember = {
            rating: dispatch.rating,
            dispatch: dispatch,
            trainingOrders: trainingOrdersMap.get(dispatch.profileId)!,
          };
          rosterMember.trainingOrders.forEach((order) => {
            const rosterRow: TrainingRosterRow = {
              rating: rosterMember.rating,
              trainingOrder: order,
              onboarded: true,
              editing: order.id ? true : false,
              profile: rosterMember.dispatch.profile!,
            };
            trainingRoster.push(rosterRow);
          });
          const officerIndex = permanentOfficers.findIndex(mariner => mariner.id === dispatch.profileId);
          if(officerIndex > -1){
            permanentOfficers.splice(officerIndex, 1);
          }
        } else {
          const trainingOrder: TrainingOrder = defaultTrainingOrder(
            dispatch.profile
          );
          const rosterRow: TrainingRosterRow = {
            rating: dispatch.rating,
            trainingOrder: trainingOrder,
            onboarded: true,
            editing: trainingOrder.id ? true : false,
            profile: dispatch.profile,
          };
          trainingRoster.push(rosterRow);
        }
      });
      permanentOfficers.forEach((permanentOfficer) => {
        if (!!permanentOfficer.id && trainingOrdersMap.has(permanentOfficer.id) &&
            !(trainingRoster.findIndex(row => row.profile.id === permanentOfficer.id) > -1)) {
          const officerTrainingOrders = trainingOrdersMap.get(permanentOfficer.id)!;
          officerTrainingOrders.forEach((order) => {
            const rosterRow: TrainingRosterRow = {
              rating: permanentOfficer.rating!,
              trainingOrder: order,
              onboarded: false,
              editing: order.id ? true : false,
              profile: permanentOfficer,
            };
            trainingRoster.push(rosterRow);
          });
        } else {
          if(!(trainingRoster.findIndex(row => row.profile.id === permanentOfficer.id) > -1)){
            const trainingOrder: TrainingOrder = defaultTrainingOrder(
              permanentOfficer
            );
            const rosterRow: TrainingRosterRow = {
              rating: permanentOfficer.rating!,
              trainingOrder: trainingOrder,
              onboarded: false,
              editing: trainingOrder.id ? true : false,
              profile: permanentOfficer,
            };
            trainingRoster.push(rosterRow);
          }
        }
      })

      let roster = trainingRoster.map((row, idx) => {
        row.id = idx;
        return row;
      });
      return sortTrainingRoster(roster);
    },
    [defaultTrainingOrder]
  );

  const sortTrainingRoster = (roster: TrainingRosterRow[]) => {
    const reorderedRatings = [
      JobRating._CHC,
      JobRating._2CB,
      JobRating._AC,
      JobRating._SA,
      JobRating._STB,
      JobRating._STW,
    ];

    return roster.sort((a, b) => {
      if (a.rating === b.rating) {
        return +Boolean(b.trainingOrder.id) - +Boolean(a.trainingOrder.id);
      } else if (
        reorderedRatings.includes(a.rating) !==
        reorderedRatings.includes(b.rating)
      ) {
        return reorderedRatings.includes(a.rating) ? 1 : -1;
      } else {
        return (
          Object.keys(JobRating).indexOf(a.rating) -
          Object.keys(JobRating).indexOf(b.rating)
        );
      }
    });
  };

  const handleAddTrainingOrder = (row: TrainingRosterRow) => {
    const newOrder = defaultTrainingOrder(row.profile);
    const newId = Math.max(...roster.map(r => r.id ?? 0)) + 1;
    const newRow: TrainingRosterRow = {
      id: newId,
      rating: row.rating,
      trainingOrder: newOrder,
      onboarded: row.onboarded,
      editing: true,
      profile: row.profile,
    };

    const updatedRoster = sortTrainingRoster([...roster, newRow]);
    setRoster(updatedRoster);
    addChangedRows(newId);
  };

  const handleRosterUpdate = (e: any, row: TrainingRosterRow, value?: any) => {
    const { id } = row;
    const tempRoster = [...roster];
    let idx = tempRoster.findIndex((r) => r.id === id);
    if (idx !== -1) {
      if (value !== undefined) {
        tempRoster[idx].trainingOrder = {
          ...tempRoster[idx].trainingOrder,
          [e.target.name]: value,
        };
      } else {
        tempRoster[idx].trainingOrder = {
          ...tempRoster[idx].trainingOrder,
          [e.target.name]: e.target.value,
        };
      }
      addChangedRows(id as number);
    }
    setRoster(tempRoster);
  };

  const validateTrainingOrder = (newTrainingOrder: TrainingOrder) => {
    if (
      newTrainingOrder.dueDate === undefined ||
      newTrainingOrder.dueDate === ""
    ) {
      return false;
    }
    if (
      !isCaptain && !isTrainingAssistant &&
      newTrainingOrder.trainingLocation === undefined &&
      newTrainingOrder.locationId === undefined
    ) {
      return false;
    }
    if (
      newTrainingOrder.training === undefined &&
      newTrainingOrder.trainingId === undefined
    ) {
      return false;
    }
    return true;
  };

  const handleSubmit = (e: FormEvent) => {
    if (props.formSubmitted === true) {
      return;
    }
    props.setFormSubmitted(true);
    e.preventDefault();
    const tempRoster = [...roster];
    const rosterWithNewTrainingOrders = tempRoster
      .filter((row) => {
        return (
          changedRows.includes(row.id as number) &&
          row.editing &&
          row.trainingOrder !== null
        );
      })
      .map((row) => {
        row.trainingOrder.rating = row.rating;
        row.trainingOrder.mariner = row.profile;
        return row;
      });
    let errors: number[] = [];

    for (var i = 0; i < rosterWithNewTrainingOrders.length; i++) {
      if (
        !validateTrainingOrder(rosterWithNewTrainingOrders[i].trainingOrder)
      ) {
        errors.push(rosterWithNewTrainingOrders[i].id as number);
      }
    }

    submitForm(
      rosterWithNewTrainingOrders.map((row) => row.trainingOrder),
      errors
    );
  };

  async function submitForm(trainingOrders: TrainingOrder[], errors: number[]) {
    if (trainingOrders.length === 0) {
      props.parentCallback({ hasInfo: true });
    } else if (errors.length > 0) {
      props.parentCallback({ hasError: true, errors });
    } else {
      let saveResult: {
        data: {
          successCount: number;
          failedCount: number;
          additionalError?: string;
        };
        hasData: boolean;
      } = {
        data: { successCount: 0, failedCount: 0 },
        hasData: false,
      };
      if (trainingOrders.length !== 0) {
        const result = JSON.parse(
          JSON.stringify(
            await saveTrainingOrders({ trainingOrders: trainingOrders })
          )
        );
        saveResult.data.successCount += result.data.successCount;
        saveResult.data.failedCount += result.data.failedCount;

        saveResult.data.additionalError = result.data.additionalError;
        saveResult.hasData = true;
      }
      props.parentCallback(saveResult);
      refetchOrders();
    }
  }

  useEffect(() => {
    (async () => {
      if (
        !isDispatchesFetching &&
        !isDispatchesLoading &&
        !isOrdersFetching &&
        !isOrdersLoading &&
        !isVesselFetching &&
        !isVesselLoading &&
        !isPermanentOfficersFetching &&
        !isPermanentOfficersLoading &&
        !!id
      ) {
        const newRoster = convertTrainingOrdersAndDispatchesToRoster(
          trainingOrders as TrainingOrder[],
          dispatches as Dispatch[],
          permanentOfficers as MarinerProfile[]
        );
        setRoster(newRoster);
      }
    })();
  }, [
    id,
    convertTrainingOrdersAndDispatchesToRoster,
    isDispatchesFetching,
    isDispatchesLoading,
    isOrdersFetching,
    isOrdersLoading,
    isVesselFetching,
    isVesselLoading,
    isPermanentOfficersFetching,
    isPermanentOfficersLoading,
    trainingOrders,
    dispatches,
    permanentOfficers,
  ]);

  usePrompt(
    "Changes will not be saved, would you like to leave?",
    changedRows.length > 0
  );

  return (
    <div>
      <form id="trainingRosterForm" onSubmit={handleSubmit} />
      {roster && vessel && (
        <TableContainer className="roster-table training-roster-component">
          <Table className="table" style={{ width: "100vw" }}>
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  style={{ width: "280px", textAlign: "left" }}
                >
                  Rating
                </TableCell>
                <TableCell style={{ width: "500px", textAlign: "left" }}>
                  Mariner's Name
                </TableCell>
                {!isViewOnly && (<TableCell align="center">Add New Order</TableCell>)}
                <TableCell align="center">Onboard</TableCell>
                <TableCell align="center">Selected</TableCell>
                <TableCell
                  align="center"
                  style={{ width: `150px`, textAlign: "left" }}
                >
                  Training Order ID
                </TableCell>
                <TableCell
                  align="center"
                  style={{ width: "260px", textAlign: "left" }}
                >
                  Training Course Expiration Date
                </TableCell>
                <TableCell align="center">Training Status</TableCell>
                <TableCell
                  className="medium-field required"
                  style={{ width: "200px" }}
                >
                  Training Needed Date
                </TableCell>
                <TableCell className="medium-field required">
                  Location
                </TableCell>
                <TableCell className="medium-field required">
                  Training
                </TableCell>
                <TableCell style={{ width: "1000px" }}>Comments</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roster &&
                roster.map((row, index) => {
                  return (
                    <TableRow key={"trainingOrder" + index}>
                      <TableCell className="left wb-wrap">
                        <div>
                          <Tooltip
                            title={row.rating ? JobRatingLabel[row.rating] : ""}
                            placement="right"
                          >
                            <span>
                              {row.rating ? row.rating.substring(1) : ""}
                            </span>
                          </Tooltip>
                        </div>
                      </TableCell>
                      <TableCell>
                        {isViewOnly || isCaptain || isTrainingAssistant ? (
                          <p>
                            {[
                              row.trainingOrder?.mariner!.firstName,
                              row.trainingOrder?.mariner!.middleName,
                              row.trainingOrder?.mariner!.lastName,
                            ].join(" ")}
                          </p>
                        ) : (
                          <NavLink
                            className="blue-link table-link"
                            style={{ textAlign: "left" }}
                            to={`/profiles/${row.trainingOrder?.mariner!.id}`}
                          >
                            {[
                              row.trainingOrder?.mariner!.firstName,
                              row.trainingOrder?.mariner!.middleName,
                              row.trainingOrder?.mariner!.lastName,
                            ].join(" ")}
                          </NavLink>
                        )}
                      </TableCell>
                      {!isViewOnly && (
                        <TableCell align="center">
                          {row.trainingOrder.id && (
                            <Button
                            size="small"
                            onClick={() => handleAddTrainingOrder(row)}
                            startIcon={<Add />}
                            >
                            </Button>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <Checkbox
                          name="onboard"
                          checked={row.onboarded}
                          onChange={(e) =>
                            toggleRowEdit(row, e.target.checked ? true : false)
                          }
                          disabled={true}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          name="selected"
                          checked={row.editing}
                          onChange={(e) =>
                            toggleRowEdit(row, e.target.checked ? true : false)
                          }
                          disabled={
                            isViewOnly || row.trainingOrder.id !== undefined
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        {row.trainingOrder.id ? (
                          isViewOnly || isCaptain || isTrainingAssistant ? (
                            <p>T-{row.trainingOrder.id}</p>
                          ) : (
                            <Link
                              className="blue-link"
                              to={`/trainingOrders/${row.trainingOrder.id}`}
                              target="_blank"
                            >
                              T-{row.trainingOrder.id}
                            </Link>
                          )
                        ) : (
                          ""
                        )}
                      </TableCell>
                      <TableCell>
                        {row.trainingOrder.expirationDate
                          ? formatDateString(row.trainingOrder.expirationDate)
                          : ""}
                      </TableCell>
                      <TableCell>
                        {row.trainingOrder.status ===
                        TrainingOrderStatus.EXPIRED ? (
                          <span className="expired-flag">
                            {TrainingOrderStatusLabel.EXPIRED}
                          </span>
                        ) : row.trainingOrder.status ===
                          TrainingOrderStatus.COMPLETED ? (
                          <span className="reg">
                            {TrainingOrderStatusLabel.COMPLETED}
                          </span>
                        ) : row.trainingOrder.status ===
                          TrainingOrderStatus.PENDING_CONFIRMATION ? (
                          <span className="reg-pending">
                            {TrainingOrderStatusLabel.PENDING_CONFIRMATION}
                          </span>
                        ) : row.trainingOrder.status ===
                          TrainingOrderStatus.TRAINING_NEEDED ? (
                          <span className="training-needed-flag">
                            {TrainingOrderStatusLabel.TRAINING_NEEDED}
                          </span>
                        ) : row.trainingOrder.status ===
                          TrainingOrderStatus.SCHEDULED ? (
                          <span className="training-needed-flag">
                            {TrainingOrderStatusLabel.SCHEDULED}
                          </span>
                        ) :row.trainingOrder.status ===
                          TrainingOrderStatus.REQUESTED ? (
                          <span className="training-needed-flag">
                            {TrainingOrderStatusLabel.REQUESTED}
                          </span>
                        ) :(
                          ""
                        )}
                      </TableCell>
                      <TableCell>
                        {row.editing ? (
                          <TextField
                            className="date-input"
                            error={
                              (row.trainingOrder?.dueDate === undefined ||
                                row.trainingOrder?.dueDate === "") &&
                              props.changedRowsWithError.includes(
                                row.id as number
                              )
                            }
                            helperText={
                              (row.trainingOrder?.dueDate === undefined ||
                                row.trainingOrder?.dueDate === "") &&
                              props.changedRowsWithError.includes(
                                row.id as number
                              ) &&
                              "Training Needed Date is required."
                            }
                            name="dueDate"
                            value={
                              row.trainingOrder?.dueDate === null
                                ? ""
                                : row.trainingOrder?.dueDate
                            }
                            type="date"
                            disabled={isViewOnly}
                            onChange={(e) => handleRosterUpdate(e, row)}
                          />
                        ) : (
                          row.trainingOrder?.dueDate
                        )}
                      </TableCell>
                      <TableCell>
                        {row.editing && locations && names ? (
                          <FormControl
                            style={{ textAlign: "left" }}
                            error={
                              row.trainingOrder?.trainingLocation ===
                                undefined &&
                              row.trainingOrder?.locationId === undefined &&
                              props.changedRowsWithError.includes(
                                row.id as number
                              )
                            }
                          >
                            <Select
                              value={
                                !!row.trainingOrder.locationId
                                  ? row.trainingOrder.locationId
                                  : ``
                              }
                              name="locationId"
                              style={{ width: "250px" }}
                              disabled={isViewOnly || isCaptain || isTrainingAssistant}
                              onChange={(e) => handleRosterUpdate(e, row)}
                            >
                              {!!locations &&
                                locations.map((location) => (
                                  <MenuItem
                                    key={location.id}
                                    value={location.id}
                                  >
                                    {location.name}
                                  </MenuItem>
                                ))}
                            </Select>
                            {!isCaptain && !isTrainingAssistant && row.trainingOrder?.trainingLocation ===
                              undefined &&
                              row.trainingOrder?.locationId === undefined &&
                              props.changedRowsWithError.includes(
                                row.id as number
                              ) && (
                                <FormHelperText>
                                  Location is required.
                                </FormHelperText>
                              )}
                          </FormControl>
                        ) : (
                          row.trainingOrder.trainingLocation?.name
                        )}
                      </TableCell>
                      <TableCell>
                        {row.editing && names ? (
                          <FormControl
                            style={{ textAlign: "left" }}
                            error={
                              row.trainingOrder?.training === undefined &&
                              row.trainingOrder?.trainingId === undefined &&
                              props.changedRowsWithError.includes(
                                row.id as number
                              )
                            }
                          >
                            <Select
                              value={
                                row.trainingOrder.trainingId !== undefined
                                  ? row.trainingOrder.trainingId
                                  : ``
                              }
                              name="trainingId"
                              autoComplete="true"
                              style={{ width: `400px` }}
                              disabled={isViewOnly}
                              onChange={(e) => handleRosterUpdate(e, row)}
                            >
                              {!!names &&
                                names.map((name) => (
                                  <MenuItem key={name.id} value={name.id}>
                                    {name.name}
                                  </MenuItem>
                                ))}
                            </Select>
                            {row.trainingOrder?.training === undefined &&
                              row.trainingOrder?.trainingId === undefined &&
                              props.changedRowsWithError.includes(
                                row.id as number
                              ) && (
                                <FormHelperText>
                                  Training is required.
                                </FormHelperText>
                              )}
                          </FormControl>
                        ) : (
                          row.trainingOrder.training?.name
                        )}
                      </TableCell>
                      <TableCell>
                        {row.editing ? (
                          <input
                            name="comments"
                            value={
                              row.trainingOrder?.comments === null
                                ? ""
                                : row.trainingOrder?.comments
                            }
                            type="text"
                            disabled={isViewOnly}
                            style={{ width: "500px" }}
                            onChange={(e) => handleRosterUpdate(e, row)}
                          />
                        ) : (
                          <p>{row.trainingOrder.comments}</p>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};

export default TrainingRosterChangeForm;
